import { mat4, ReadonlyVec3, vec3 } from 'gl-matrix';
import { DvfField, dvfDisplacementAt } from './dvfField';

export interface DeformableTransform {
    readonly field: DvfField;
    readonly pre: mat4;
    readonly post: mat4;
}

export function mapPointThroughDeformation(
    transform: DeformableTransform,
    point: ReadonlyVec3
): vec3 | undefined {
    const preImage = vec3.transformMat4(vec3.create(), point, transform.pre);
    const displacement = dvfDisplacementAt(transform.field, preImage);
    if (!displacement) {
        return undefined;
    }
    const displaced = vec3.add(displacement, preImage, displacement);
    return vec3.transformMat4(displaced, displaced, transform.post);
}

const REVERSE_MAX_STEPS = 16;

const REVERSE_TOLERANCE_MM = 0.01;

export function mapPointBackThroughDeformation(
    transform: DeformableTransform,
    point: ReadonlyVec3
): vec3 | undefined {
    const postInverse = mat4.invert(mat4.create(), transform.post);
    const preInverse = mat4.invert(mat4.create(), transform.pre);
    if (!postInverse || !preInverse) {
        return undefined;
    }
    const target = vec3.transformMat4(vec3.create(), point, postInverse);
    const q = vec3.clone(target);
    for (let step = 0; step < REVERSE_MAX_STEPS; step++) {
        const displacement = dvfDisplacementAt(transform.field, q);
        if (!displacement) {
            return undefined;
        }
        const next = vec3.subtract(vec3.create(), target, displacement);
        const moved = vec3.distance(next, q);
        vec3.copy(q, next);
        if (moved <= REVERSE_TOLERANCE_MM) {
            return vec3.transformMat4(q, q, preInverse);
        }
    }
    // Not converged: the field is not contracting here, so there is no trustworthy inverse.
    return undefined;
}
